import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Einfacher, regelbasierter FAQ-Bot: keine LLM-API, keine laufenden Kosten.
// Jedes Thema hat Stichwörter in EN+FR, damit Freitext-Fragen automatisch
// erkannt werden; die Antworttexte selbst kommen aus LanguageContext, damit
// sie in der Sprache erscheinen, die der Nutzer gerade ausgewählt hat.
const FAQ_TOPICS = [
  // Hotel-Reservierung: spezielle Fragen zuerst, damit z. B. "hôtel pdf" nicht beim
  // allgemeinen Thema landet. chip: false = nur per Freitext, kein Schnellwahl-Button.
  {
    id: 'hotel_delivery',
    labelKey: 'chat_topic_hotel_delivery',
    answerKey: 'chat_answer_hotel_delivery',
    chip: false,
    keywords: ['pdf', 'when will i', 'how long', 'combien de temps', 'quand', 'délai', 'delai', 'livraison', 'delivery', 'receive my hotel', 'recevoir ma réservation'],
    requires: ['hotel', 'hôtel', 'réservation', 'reservation', 'pdf', 'justificatif'],
  },
  {
    id: 'hotel_real',
    labelKey: 'chat_topic_hotel_real',
    answerKey: 'chat_answer_hotel_real',
    chip: false,
    keywords: ['real', 'fake', 'vrai', 'faux', 'fausse', 'vérifiable', 'verifiable', 'accept', 'refus', 'refuse', 'cancel', 'annul', 'keep', 'garder', 'dummy'],
    requires: ['hotel', 'hôtel', 'réservation', 'reservation', 'booking'],
  },
  {
    id: 'hotel',
    labelKey: 'chat_topic_hotel',
    answerKey: 'chat_answer_hotel',
    keywords: ['hotel', 'hôtel', 'hébergement', 'hebergement', 'accommodation', 'logement', 'réservation', 'reservation', 'booking', 'justificatif', 'proof of'],
  },
  {
    id: 'payment_tn',
    labelKey: 'chat_topic_payment_tn',
    answerKey: 'chat_answer_payment_tn',
    keywords: ['dhmad', 'tunisia', 'tunisie', 'tnd', 'escrow', 'sequestre', 'séquestre'],
  },
  {
    id: 'payment_intl',
    labelKey: 'chat_topic_payment_intl',
    answerKey: 'chat_answer_payment_intl',
    keywords: ['stripe', 'card', 'carte', 'credit card', 'crédit', 'mastercard', 'visa card', 'payment', 'paiement', 'pay', 'payer'],
  },
  {
    id: 'how_alerts',
    labelKey: 'chat_topic_how_alerts',
    answerKey: 'chat_answer_how_alerts',
    keywords: ['alert', 'alerte', 'sms', 'notification', 'how do i get', 'comment je re', 'receive', 'recevoir'],
  },
  {
    id: 'refund',
    labelKey: 'chat_topic_refund',
    answerKey: 'chat_answer_refund',
    keywords: ['refund', 'remboursement', 'rembourser', 'money back', '30 day', '30 jour'],
  },
  {
    id: 'what_is',
    labelKey: 'chat_topic_what_is',
    answerKey: 'chat_answer_what_is',
    keywords: ["what is visaalerts", "qu'est-ce", 'tls', 'vfs', 'bls', 'affiliat', 'depend'],
  },
];

function matchTopic(rawText) {
  const text = rawText.toLowerCase();
  for (const topic of FAQ_TOPICS) {
    const required = !topic.requires || topic.requires.some((k) => text.includes(k));
    if (required && topic.keywords.some((k) => text.includes(k))) return topic;
  }
  return null;
}

function getSessionId() {
  const STORAGE_KEY = 'visaalerts_chat_session_id';
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch (e) {
    // localStorage evtl. blockiert (privates Fenster) -> flüchtige Session-ID
    return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * page: 'landing' | 'dashboard' — nur zur Einordnung im Admin-Chatverlauf.
 */
export default function ChatWidget({ page }) {
  const { t, language } = useLanguage();
  // AuthProvider wrapt die ganze App global (siehe App.js), daher ist der
  // Hook hier immer sicher aufrufbar -- kein try/catch nötig (und laut
  // react-hooks/rules-of-hooks auch nicht erlaubt).
  const { user } = useAuth();
  const userEmail = user?.email || null;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const sessionIdRef = useRef(getSessionId());
  const hasWelcomedRef = useRef(false);
  const scrollEndRef = useRef(null);

  useEffect(() => {
    if (open && !hasWelcomedRef.current) {
      hasWelcomedRef.current = true;
      pushBotMessage(t('chat_welcome'), null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const logMessage = async (sender, text, topic) => {
    try {
      await axios.post(`${API}/chat/log`, {
        session_id: sessionIdRef.current,
        page,
        sender,
        message: text,
        topic: topic || null,
        user_email: userEmail,
        language,
      });
    } catch (e) {
      // Logging darf den Chat nie blockieren
    }
  };

  const pushBotMessage = (text, topicId) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-b`, sender: 'bot', text }]);
    logMessage('bot', text, topicId);
  };

  const pushUserMessage = (text, topicId) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-u`, sender: 'user', text }]);
    logMessage('user', text, topicId);
  };

  const handleTopicClick = (topic) => {
    pushUserMessage(t(topic.labelKey), topic.id);
    setTimeout(() => pushBotMessage(t(topic.answerKey), topic.id), 350);
  };

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text) return;
    pushUserMessage(text, null);
    setInputValue('');
    const matched = matchTopic(text);
    setTimeout(() => {
      if (matched) {
        pushBotMessage(t(matched.answerKey), matched.id);
      } else {
        pushBotMessage(t('chat_fallback'), null);
      }
    }, 450);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#FF3B30] text-white shadow-lg hover:bg-[#E5352B] transition-colors"
        aria-label={t('chat_button_label')}
        data-testid="chat-widget-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 flex h-[70vh] max-h-[420px] w-[80vw] max-w-[320px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-[#0A0A0A] px-4 py-3 text-white">
            <span className="font-heading font-semibold text-sm">{t('chat_header_title')}</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('chat_close')}
              className="text-gray-300 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 px-3 py-3">
            <div className="flex flex-col gap-2">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-snug whitespace-pre-wrap ${
                    m.sender === 'user'
                      ? 'self-end bg-[#FF3B30] text-white rounded-br-sm'
                      : 'self-start bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              ))}
              <div ref={scrollEndRef} />
            </div>
          </ScrollArea>

          {/* Quick-reply topics */}
          <div className="flex flex-wrap gap-1.5 border-t border-gray-100 px-3 py-2">
            {FAQ_TOPICS.filter((topic) => topic.chip !== false).map((topic) => (
              <button
                key={topic.id}
                type="button"
                onClick={() => handleTopicClick(topic)}
                className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:border-[#FF3B30]/50 hover:text-[#FF3B30] transition-colors"
              >
                {t(topic.labelKey)}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 border-t border-gray-100 p-2.5">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chat_input_placeholder')}
              className="flex-1 h-9 text-sm"
              data-testid="chat-widget-input"
            />
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              size="icon"
              className="h-9 w-9 shrink-0 bg-[#FF3B30] hover:bg-[#E5352B]"
              aria-label={t('chat_send')}
              data-testid="chat-widget-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
